import { location } from 'cdui-js';

/**
 * RNBridge 统一分发器
 *
 * 问题背景：原生 App 通过 `window.RNBridge.receiveFromRN(msg)` 向 H5 推送消息，
 * 但直接赋值 `receiveFromRN` 会导致后赋值的处理器覆盖前一个。
 * 该模块提供多播机制：任意模块均可注册/注销处理器，互不影响。
 *
 * 用法：
 *   1. 在应用启动时调用一次 `initRNBridge()`（幂等）
 *   2. 在需要处理消息的模块调用 `registerRNHandler(fn)` 获取注销函数
 */

export type RNMessage = { type: string; requestId?: string; payload?: unknown; timestamp?: number };
type Handler = (msg: RNMessage) => void;

type UserTokenPayload = {
  token?: string;
  tokenHead?: string;
  refreshToken?: string;
};

const _handlers = new Set<Handler>();

let _bridgeInitialized = false;

/**
 * 本次 WebView 会话是否已收到 APP_TO_WEB_UserToken（含非空 token）。
 * 不能用 localStorage 是否有值判断：残留旧 token 会误放行鉴权请求。
 */
let _sessionRNTokenReady = false;

/** 脱敏：只保留尾 8 位，便于与 App Metro 日志对齐比对 */
export function maskTail(s?: string | null): string | null {
  if (!s || typeof s !== 'string') return null;
  return s.length <= 8 ? s : '…' + s.slice(-8);
}

/** WebView 等待原生 token 超时（无 token 不放行，避免后续接口 401） */
export class RNTokenTimeoutError extends Error {
  readonly code = 'RN_TOKEN_TIMEOUT';

  constructor(message = 'RN token timeout') {
    super(message);
    this.name = 'RNTokenTimeoutError';
  }
}

export const isRNTokenTimeoutError = (e: unknown): e is RNTokenTimeoutError =>
  e instanceof RNTokenTimeoutError ||
  (typeof e === 'object' && e != null && (e as { code?: string }).code === 'RN_TOKEN_TIMEOUT');

/**
 * 将 App 下发的 token 写入 localStorage。
 * @returns true = 写入了新 token；false = 无 token 或与现有相同（去重）
 */
export function applyRNUserToken(token?: string | null): boolean {
  if (!token) return false;
  if (localStorage.getItem('token') === token) return false;
  localStorage.setItem('token', token);
  return true;
}

function markSessionRNTokenReady(token?: string | null): boolean {
  if (!token) return false;
  applyRNUserToken(token);
  _sessionRNTokenReady = true;
  return !!localStorage.getItem('token');
}

/**
 * 其它通道（如 APP_TO_WEB_TOKEN）已写入最新 token 时调用，
 * 供 waitForRNToken / 敏感图鉴权立即放行，无需再等 APP_TO_WEB_UserToken。
 */
export function notifySessionRNTokenReady(token?: string | null): boolean {
  return markSessionRNTokenReady(token);
}

function persistTokenFromMessage(msg: RNMessage) {
  if (msg?.type !== 'APP_TO_WEB_UserToken') return;
  const { token, tokenHead, refreshToken } = (msg.payload as UserTokenPayload | undefined) || {};
  console.log('[H5][App→H5] APP_TO_WEB_UserToken', {
    tokenHead: tokenHead ?? null,
    tokenLen: token?.length ?? 0,
    tokenTail: maskTail(token),
    refreshLen: refreshToken?.length ?? 0,
    refreshTail: maskTail(refreshToken),
  });
  // 即使与旧 token 相同，也标记本会话已收到 App 下发
  markSessionRNTokenReady(token);
}

/**
 * App 注入常连续调用 receiveFromRN + __rnInternalReceive。
 * 二者现均指向本分发器，同一条消息（同 type + timestamp）必须只处理一次，
 * 否则开户 APP_TO_WEB_GoBack 会 lastProcess 两次，一次退两步。
 */
let _dispatchDedupeKey = '';
let _dispatchDedupeAt = 0;
const DISPATCH_DEDUPE_MS = 50;

function dispatchDedupeKey(msg: RNMessage): string {
  return `${msg?.type ?? ''}:${msg?.timestamp ?? ''}`;
}

/**
 * APP_TO_WEB_GoTo：App 让当前 WebView 跳到 H5 路由。
 * payload.url 为项目内路径；payload.params 合并进 query，同名键覆盖 url 上已有参数。
 */
function goToFromApp(payload: unknown) {
  const data = (payload && typeof payload === 'object' ? payload : {}) as {
    url?: string;
    params?: Record<string, unknown>;
  };
  const raw = String(data.url || '').trim();
  if (!raw || /^https?:\/\//i.test(raw)) return;

  const hashAt = raw.indexOf('#');
  const hash = hashAt >= 0 ? raw.slice(hashAt) : '';
  const withoutHash = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
  const queryAt = withoutHash.indexOf('?');
  const path = queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash;
  const search = new URLSearchParams(queryAt >= 0 ? withoutHash.slice(queryAt + 1) : '');
  const params =
    data.params && typeof data.params === 'object' && !Array.isArray(data.params) ? data.params : {};

  Object.entries(params).forEach(([key, value]) => {
    if (!key || value == null) return;
    search.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  });

  const qs = search.toString();
  const target = `${path}${qs ? `?${qs}` : ''}${hash}`;
  console.log('[H5][App→H5] APP_TO_WEB_GoTo', { url: raw, target });
  location.routeTo(target, [0, 0]);
}

function dispatchRNMessage(msg: RNMessage) {
  const key = dispatchDedupeKey(msg);
  const now = Date.now();
  if (key === _dispatchDedupeKey && now - _dispatchDedupeAt < DISPATCH_DEDUPE_MS) {
    return;
  }
  _dispatchDedupeKey = key;
  _dispatchDedupeAt = now;

  // 先落盘 token，再通知 waiters/handlers，避免「事件到了但 Authorization 仍为空」
  persistTokenFromMessage(msg);

  if (msg?.type === 'APP_TO_WEB_GoTo') {
    goToFromApp(msg.payload);
  }

  // 拷贝后再遍历：handler 内若 routeReplace/挂载又 register，新 handler 不应吃到当前这条消息
  // （否则 pending-list 回银行列表时，新挂上的 List Shell 会再 goBack 一次直接出栈）
  [..._handlers].forEach((h) => {
    try {
      h(msg);
    } catch {}
  });

  // 兼容 index.html / App.tsx 等基于 CustomEvent 的旧监听（如 APP_TO_WEB_UserToken）
  if (msg?.type && typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(msg.type, { detail: msg }));
    } catch {}
  }
}

/** 初始化 Bridge（幂等，可多次调用）—— 把 window.RNBridge.receiveFromRN 指向多播分发器 */
export function initRNBridge() {
  const w = window as any;
  w.RNBridge = w.RNBridge || {};
  w.RNBridge.receiveFromRN = dispatchRNMessage;
  w.RNBridge.__rnInternalReceive = dispatchRNMessage;
  _bridgeInitialized = true;
}

/** 注册消息处理器，返回注销函数 */
export function registerRNHandler(fn: Handler): () => void {
  if (!_bridgeInitialized) {
    initRNBridge();
  }
  _handlers.add(fn);
  return () => _handlers.delete(fn);
}

/** 全站共用：多次调用返回同一 Promise；成功后一直复用；失败后可再次 wait 重试 */
let _tokenReady: Promise<void> | null = null;

function isSessionTokenReady(): boolean {
  return _sessionRNTokenReady && !!localStorage.getItem('token');
}

function createRNTokenWait(timeoutMs: number): Promise<void> {
  // 纯 H5：不阻塞；走本地登录 token（可能尚无，由后续登录流程处理）
  if (!(window as any).ReactNativeWebView) {
    return Promise.resolve();
  }

  // WebView：必须等本次 APP_TO_WEB_UserToken；禁止仅凭旧 localStorage 放行
  if (isSessionTokenReady()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (timer !== undefined) clearTimeout(timer);
      unsubHandler();
      window.removeEventListener('APP_TO_WEB_UserToken', onCustomEvent);
    };

    const finishOk = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const finishFail = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new RNTokenTimeoutError());
    };

    /** 仅在本会话已收到 App 下发且 token 已写入后结束 */
    const tryFinishWithToken = (token?: string) => {
      if (markSessionRNTokenReady(token) || isSessionTokenReady()) {
        finishOk();
      }
    };

    const onCustomEvent = (e: Event) => {
      const token = (e as CustomEvent<{ payload?: { token?: string } }>).detail?.payload?.token;
      tryFinishWithToken(token);
    };

    const unsubHandler = registerRNHandler((msg) => {
      if (msg.type === 'APP_TO_WEB_UserToken') {
        tryFinishWithToken((msg.payload as { token?: string } | undefined)?.token);
      }
    });

    window.addEventListener('APP_TO_WEB_UserToken', onCustomEvent);

    // 注册监听后 dual-check：App 可能在 wait 启动前已下发
    if (isSessionTokenReady()) {
      finishOk();
      return;
    }

    timer = setTimeout(() => {
      // 超时仍未收到本次 App 下发：不放行（即使 localStorage 有旧 token）
      if (isSessionTokenReady()) {
        finishOk();
        return;
      }
      finishFail();
    }, timeoutMs);
  });
}

/**
 * 在 App WebView 内等待原生注入 token。
 * 全站单例：bootstrap / 业务页共用，必须 await 完成后再打鉴权接口。
 *
 * - 非 WebView：立即 resolve（走本地登录 token）
 * - WebView：必须等到本次 APP_TO_WEB_UserToken 写入；残留旧 token 不算就绪
 * - 超时仍未收到本次下发则 reject（不放行，避免用旧 token 打出 430）
 * - 失败后再次调用可重新等待（便于重试）
 */
export function waitForRNToken(timeoutMs = 8000): Promise<void> {
  if (_tokenReady) return _tokenReady;

  _tokenReady = createRNTokenWait(timeoutMs).catch((err) => {
    _tokenReady = null;
    throw err;
  });

  return _tokenReady;
}

/** 向原生 App 发送消息 */
export function postToApp(message: { type: string; requestId?: string; payload?: unknown; timestamp?: number }) {
  (window as any).ReactNativeWebView?.postMessage(JSON.stringify(message));
}
